import { useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { Table, Tag, Typography, Tooltip } from "antd";

import ContextMenu from "@/components/containers/ContextMenu";
import EmployeeProfile from "@/components/cards/EmployeeProfile";
import CustomLink from "@/components/ui/CustomLink";

import { UserContext } from "@/context/UserContext";

import routes from "@/routes";

import { formateDate, getPluralizedWord } from "@/utils";

import { STATUS_OPTIONS } from "@/constants/sprints";
import { useColumnConfig } from "@/hooks/useColumnConfig";

const LOCAL_STORAGE_KEY = "sprints_table_columns";

const COLUMN_LABELS = {
  name: "Название",
  status: "Статус",
  time_in_current_status_sec: "В этом статусе",
  author: "Автор",
  start_date: "Дата начала",
  end_date: "Дата окончания",
  unit: "Рабочая группа",
};

const calcSortOrder = (sortedInfo, field) => {
  const col = sortedInfo.find(({ columnKey }) => columnKey === field);
  return col ? col.order : null;
};

const SprintsTable = ({
  data,
  loading,
  pagination,
  sortedInfo,
  unit = null,
  isAdmins = false,
  resize = false,
  scroll = { x: "100%" },
  onEditItem,
  onDeleteItem,
  onChange,
}) => {
  const { user } = useContext(UserContext);

  const navigate = useNavigate();

  const baseColumns = useMemo(() => {
    const getHref = (uuid, unit_uuid) => {
      return isAdmins
        ? routes.administration.sprintDetail(uuid)
        : unit
        ? routes.unitSprintDetail(unit_uuid, uuid)
        : routes.sprintDetail(uuid);
    };
    return [
      {
        title: COLUMN_LABELS.name,
        dataIndex: "name",
        key: "name",
        sortOrder: calcSortOrder(sortedInfo, "name"),
        sorter: true,
        width: 300,
        render: (name, record) => (
          <CustomLink
            to={getHref(record.uuid, record.unit_uuid)}
            color="#303030"
          >
            <Tooltip placement="bottomLeft" title={name}>
              <Typography.Paragraph
                ellipsis={{ rows: 2 }}
                style={{
                  color: "inherit",
                  lineHeight: 1.4,
                  marginBottom: 0,
                  display: "-webkit-inline-box",
                }}
              >
                {name}
              </Typography.Paragraph>
            </Tooltip>
          </CustomLink>
        ),
      },
      {
        title: COLUMN_LABELS.status,
        width: 150,
        dataIndex: "status",
        key: "status.idx",
        sortOrder: calcSortOrder(sortedInfo, "status.idx"),
        sorter: true,
        render: (status) => {
          const icon = STATUS_OPTIONS.find(
            (item) => item.value === status.style_key
          )?.icon;

          return (
            <Tag
              icon={icon}
              color={status.style_key}
              className="tag-with-icon-color"
            >
              {status.name}
            </Tag>
          );
        },
      },
      {
        title: COLUMN_LABELS.time_in_current_status_sec,
        width: 130,
        dataIndex: "time_in_current_status_sec",
        key: "time_in_current_status_sec",
        sortOrder: calcSortOrder(sortedInfo, "time_in_current_status_sec"),
        sorter: true,
        render: (time) => {
          const duration = Math.floor(time / 86400); // секунды в дне

          if (duration <= 0) return <span>менее дня</span>;

          return (
            <span>
              {duration} {getPluralizedWord(duration, ["день", "дня", "дней"])}
            </span>
          );
        },
      },
      {
        title: COLUMN_LABELS.author,
        dataIndex: "author",
        key: "author.fullname",
        sortOrder: calcSortOrder(sortedInfo, "author.fullname"),
        sorter: true,
        width: 180,
        ellipsis: { showTitle: false },
        render: (author) => {
          return (
            <EmployeeProfile
              {...author}
              fullName={false}
              avatarSize={24}
              tooltip
            />
          );
        },
      },
      {
        title: COLUMN_LABELS.start_date,
        width: 110,
        dataIndex: "start_date",
        key: "start_date",
        sortOrder: calcSortOrder(sortedInfo, "start_date"),
        sorter: true,
        render: (start_date) => {
          return formateDate(start_date);
        },
      },
      {
        title: COLUMN_LABELS.end_date,
        width: 110,
        dataIndex: "end_date",
        key: "end_date",
        sortOrder: calcSortOrder(sortedInfo, "end_date"),
        sorter: true,
        render: (end_date) => {
          return formateDate(end_date);
        },
      },
      {
        title: COLUMN_LABELS.unit,
        width: 150,
        dataIndex: "unit",
        key: "unit.name",
        sortOrder: calcSortOrder(sortedInfo, "unit.name"),
        sorter: true,
        ellipsis: { showTitle: false },
        render: (unit) => (
          <Tooltip placement="bottomLeft" title={unit?.name}>
            {unit?.name}
          </Tooltip>
        ),
      },
      {
        title: "",
        width: 48,
        align: "center",
        fixed: "right",
        key: "actions",
        render: (_, item) => {
          return item.canManage || isAdmins ? (
            <ContextMenu
              vertical
              onView={() => navigate(getHref(item.uuid, item.unit_uuid))}
              onEdit={() => onEditItem(item)}
              onDelete={() => onDeleteItem(item)}
            />
          ) : (
            <ContextMenu
              vertical
              onView={() => navigate(getHref(item.uuid, item.unit_uuid))}
            />
          );
        },
      },
    ].filter(Boolean);
  }, [navigate, onEditItem, onDeleteItem, isAdmins, unit, sortedInfo]);

  const { columns, components } = useColumnConfig(
    baseColumns,
    user.user_uuid,
    LOCAL_STORAGE_KEY,
    {
      resize,
      columnLabels: COLUMN_LABELS,
    }
  );

  return (
    <Table
      dataSource={data}
      columns={columns}
      components={resize ? components : undefined}
      rowKey="uuid"
      loading={loading}
      pagination={pagination}
      showSorterTooltip={{
        target: "sorter-icon",
        title: "Нажмите для сортировки",
      }}
      scroll={scroll}
      tableLayout="fixed"
      bordered
      onChange={onChange}
    />
  );
};

export default SprintsTable;
